import { Link } from "@material-ui/core";
import { Grid } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { Button } from "react-bootstrap";
import { FaPen } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { buildRequestFieldDefs, initLenderRequest, projectInfoResult, resetLenderRequest, selectFieldChanges } from "../../../redux/actions/lenderRequestActions";
import EditableRequestField from "../../LenderRequests/RequestFeed/RequestDetailsModal/EditableRequestField";
import projectFieldDefs from "../../shared/constants/projectFieldDefs";
import { getGoogleAddressFields } from "../../shared/services/SharedService";
import { DelayFor } from "../../shared/utilities/promise-utils";
import RequiredDocsTooltip from "./RequiredDocsTooltip";
import { REQUEST_TYPES } from "../../shared/constants/RequestTypes";

const SUMMARY_ANALYSIS_MAX_LENGTH = 2000;

function validate(fieldDef, value) {
  const validationMessage = fieldDef.required && (value === "" || value == null)
    ? "This field is required."
    : fieldDef.validateComplete && !fieldDef.validateComplete(value)
    ? fieldDef.invalidMessage ?? "Please enter a valid value."
    : "";
  
  return validationMessage;
}

export const EditFieldsPage = ({stepCount, incrementCounter, projectInfo}) => {
  const draftChanges = useSelector((state) => state.lenderRequestReducer.editingChanges);
  const fieldDefsMap = useSelector(state => state.lenderRequestReducer.projectFieldsMap);  

  const [changes, setChanges] = useState(new Map());
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [showStyleComments, setShowStyleComments] = useState(projectInfo?.projectStyle === "Other");
  
  const [validationErrors, setValidationErrors] = useState(new Map());
  const [showValidation, setShowValidation] = useState(new Set());  

  const [summaryAnalysis, setSummaryAnalysis] = useState("");
    
  const fieldOrder = projectFieldDefs.map(field => field.fieldName);

  // Refs for address inputs to attach Google Autocomplete
  const streetInputRef = useRef(null);
  const cityInputRef = useRef(null);
  const zip5InputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const cityAutocompleteRef = useRef(null);
  const zip5AutocompleteRef = useRef(null);

  const dispatch = useDispatch();

  // Capture whether there are already-stored field changes at mount time.
  // If so, the user is returning from the Attachments step — skip the full reset
  // so the previously-entered changes survive the remount.
  const isReturningFromNextStepRef = useRef(!!draftChanges);

  useEffect(() => {
    if (!isReturningFromNextStepRef.current) {
      dispatch(resetLenderRequest());
    }
    dispatch(initLenderRequest(REQUEST_TYPES.LENDER_EDIT_PROJECT));
    dispatch(buildRequestFieldDefs());
  }, []);

  // Initialize Google Autocomplete for street address field
  useEffect(() => {
    const isStreetEditing = changes.has("street");
    
    if (!isStreetEditing || !streetInputRef?.current) {
      // Cleanup previous autocomplete if street editing stopped
      if (autocompleteRef.current && window.google?.maps?.event) {
        window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
        autocompleteRef.current = null;
      }
      return;
    }

    if (!window.google?.maps?.places) {
      console.warn('Google Maps Places API not loaded');
      return;
    }

    try {
      const autocompleteConfig = {
        componentRestrictions: { country: 'US' },
        fields: ["address_components", "geometry"],
        types: ['address']
      };

      const autocomplete = new window.google.maps.places.Autocomplete(streetInputRef.current, autocompleteConfig);

      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();
        if (!place?.address_components) return;

        const address = getGoogleAddressFields(place);
        
        // Update all address fields from Google Autocomplete
        if (address.street || address.city || address.state || address.zip) {
          handleAddressFieldsUpdate(address);
        }
      });

      autocompleteRef.current = autocomplete;
    } catch (error) {
      console.error('Error initializing Google Autocomplete:', error);
    }

    return () => {
      if (autocompleteRef.current && window.google?.maps?.event) {
        window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
      }
    };
  }, [changes.has("street"), projectInfo]);

  useEffect(() => {
    if (draftChanges && fieldDefsMap.size && projectInfo) {
      const _showValidation = new Set();
      const _validationErrors = new Map();
      const _changes = new Map();

      draftChanges.forEach(change => {
        const fieldName = change.fieldName;
        const fieldDef = fieldDefsMap.get(fieldName);

        _changes.set(fieldName, { ...change });
        _showValidation.add(fieldName);

        const validationMessage = validate(fieldDef, change.newValue);  
        if (validationMessage) {
          _validationErrors.set(fieldName, validationMessage);
        }
      });
      
      setChanges(_changes);
      setShowValidation(_showValidation);
      setValidationErrors(_validationErrors);
    }
  }, [draftChanges, projectInfo, fieldDefsMap]);

  useEffect(() => {
    if (projectInfo) {
      const hasChanges = [...changes.values()].some(change => 
        change.newValue !== projectInfo[change.fieldName]
      );

      setHasChanges(hasChanges);

      const projectStyle = changes.has("projectStyle")
        ? changes.get("projectStyle").newValue
        : projectInfo.projectStyle;

      setShowStyleComments(projectStyle === "Other");
    }
  }, [changes, projectInfo]);

  function startEditing(fieldName) {
    const oldValue = projectInfo[fieldName];

    setChanges(prev => new Map(prev).set(fieldName, {
      fieldName,
      oldValue: oldValue,
      newValue: oldValue
    }));

    const fieldDef = fieldDefsMap.get(fieldName);

    // if (fieldDef?.type === "select") {
      const validationMessage = validate(fieldDef, oldValue);

      if (validationMessage) {
        setValidationErrors(prev => new Map(prev).set(fieldName, validationMessage));
      } else {
        setValidationErrors(prev => {
          const _validationErrors = new Map(prev);
          _validationErrors.delete(fieldName);
          return _validationErrors;
        });
      }
    // }
  }

  function cancelEditing(fieldName) {
    // Use a short delay to give other events a chance to run
    DelayFor(30).then(() => {
      setChanges(prev => {
        const _changes = new Map(prev);
        _changes.delete(fieldName);
        return _changes;
      });
      
      setValidationErrors(prev => {
        const _validationErrors = new Map(prev);
        _validationErrors.delete(fieldName);
        return _validationErrors;
      });

      setShowValidation(prev => {
        const _showValidation = new Set(prev);
        _showValidation.delete(fieldName);
        return _showValidation;
      });
    });
  }

  function handleBlur(fieldName, validationMessage) {
    setShowValidation(prev => new Set(prev).add(fieldName));
  }

  function handleChanged(fieldName, modifiedValue, validationMessage) {
    // Use a short delay to give other handlers a chance to run 
    DelayFor(20).then(() => {
      setChanges(prev => new Map(prev).set(fieldName, {
        ...prev.get(fieldName),
        newValue: modifiedValue,
      }));
      
      if (validationMessage) {
        setValidationErrors(prev => new Map(prev).set(fieldName, validationMessage));
      } else {
        setValidationErrors(prev => {
          const _validationErrors = new Map(prev);
          _validationErrors.delete(fieldName);
          return _validationErrors;
        });
      }
      
      // Special handling for projectStyle - if changing projectStyle away from "Other", remove styleComments change and validation error
      if (fieldName === "projectStyle") {
        if (modifiedValue !== "Other") {
          setChanges(prev => {
            const _changes = new Map(prev);
            _changes.delete("styleComments");
            return _changes;
          });

          setValidationErrors(prev => {
            const _validationErrors = new Map(prev);
            _validationErrors.delete("styleComments");
            return _validationErrors;
          });
        }

        setShowStyleComments(modifiedValue === "Other");
      }
    });
  }

  // Update a specific subset of address fields (used by google autocomplete).
  // fieldUpdates is an object like { city, state, zip5 } – falsy values are skipped.
  function handleSpecificFieldsUpdate(fieldUpdates) {
    Object.entries(fieldUpdates).forEach(([fieldName, newValue]) => {
      if (!newValue) return;

      const oldValue = projectInfo[fieldName];
      const fieldDef = fieldDefsMap.get(fieldName);
      if (!fieldDef) return;

      setChanges(prev => {
        const currentChange = prev.get(fieldName);
        return new Map(prev).set(fieldName, {
          fieldName,
          oldValue: currentChange ? currentChange.oldValue : oldValue,
          newValue,
        });
      });

      setShowValidation(prev => new Set(prev).add(fieldName));

      const validationMessage = validate(fieldDef, newValue);
      setValidationErrors(prev => {
        const _validationErrors = new Map(prev);
        if (validationMessage) {
          _validationErrors.set(fieldName, validationMessage);
        } else {
          _validationErrors.delete(fieldName);
        }
        return _validationErrors;
      });
    });
  }

  function handleAddressFieldsUpdate(addressData) {
    const addressFields = ['street', 'city', 'state', 'zip5'];
    
    addressFields.forEach(fieldName => {
      const newValue = fieldName === "zip5" ? addressData["zip"] : addressData[fieldName];
      const oldValue = projectInfo[fieldName];
      const fieldDef = fieldDefsMap.get(fieldName);

      if (!fieldDef) {
        return;
      }
      
      // Only update if the field value is different
      if (newValue !== oldValue) {
        // Update or create field change
        setChanges(prev => {
          const currentChange = changes.get(fieldName);
          const _changes = new Map(prev).set(fieldName, {
            fieldName: currentChange ? currentChange.fieldName : fieldName,
            oldValue: currentChange ? currentChange.oldValue : oldValue,
            newValue
          });
          return _changes;
        });

        // Validate the new value
        const validationMessage = validate(fieldDef, newValue);

        setValidationErrors(prev => {
          const _validationErrors = new Map(prev);
          if (validationMessage) {
            _validationErrors.set(fieldName, validationMessage);
          } else {
            _validationErrors.delete(fieldName);
          }
          return _validationErrors;
        });
      }
    });
  }

  // Google Autocomplete for city field
  useEffect(() => {
    const isEditing = changes.has("city");

    if (!isEditing || !cityInputRef?.current) {
      if (cityAutocompleteRef.current && window.google?.maps?.event) {
        window.google.maps.event.clearInstanceListeners(cityAutocompleteRef.current);
        cityAutocompleteRef.current = null;
      }
      return;
    }

    if (!window.google?.maps?.places) {
      console.warn('Google Maps Places API not loaded');
      return;
    }

    try {
      const autocomplete = new window.google.maps.places.Autocomplete(cityInputRef.current, {
        componentRestrictions: { country: 'US' },
        fields: ["address_components"],
        types: ['(cities)'],
      });

      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();
        if (!place?.address_components) return;
        const address = getGoogleAddressFields(place);
        handleSpecificFieldsUpdate({ city: address.city, state: address.state });
      });

      cityAutocompleteRef.current = autocomplete;
    } catch (error) {
      console.error('Error initializing city Google Autocomplete:', error);
    }

    return () => {
      if (cityAutocompleteRef.current && window.google?.maps?.event) {
        window.google.maps.event.clearInstanceListeners(cityAutocompleteRef.current);
      }
    };
  }, [changes.has("city"), projectInfo]);

  // Google Autocomplete for zip5 field
  useEffect(() => {
    const isEditing = changes.has("zip5");

    if (!isEditing || !zip5InputRef?.current) {
      if (zip5AutocompleteRef.current && window.google?.maps?.event) {
        window.google.maps.event.clearInstanceListeners(zip5AutocompleteRef.current);
        zip5AutocompleteRef.current = null;
      }
      return;
    }

    if (!window.google?.maps?.places) {
      console.warn('Google Maps Places API not loaded');
      return;
    }

    try {
      const autocomplete = new window.google.maps.places.Autocomplete(zip5InputRef.current, {
        componentRestrictions: { country: 'US' },
        fields: ["address_components"],
        types: ['postal_code'],
      });

      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();
        if (!place?.address_components) return;
        const address = getGoogleAddressFields(place);
        handleSpecificFieldsUpdate({
          zip5: address.zip,
          city: address.city,
          state: address.state,
        });
      });

      zip5AutocompleteRef.current = autocomplete;
    } catch (error) {
      console.error('Error initializing ZIP Google Autocomplete:', error);
    }

    return () => {
      if (zip5AutocompleteRef.current && window.google?.maps?.event) {
        window.google.maps.event.clearInstanceListeners(zip5AutocompleteRef.current);
      }
    };
  }, [changes.has("zip5"), projectInfo]);

  async function handleSubmit(e) {
    e.preventDefault();

    // Use a short delay to give the other handlers a chance to run
    DelayFor(40).then(() => {

      setIsSubmitted(true);
      setShowValidation(new Set(changes.keys()));

      if (!hasChanges || validationErrors.size) {
        return;
      }

      const _changes = new Map(changes);

      _changes.forEach((change, fieldName) => {
        if (change.newValue === projectInfo[fieldName]) {
          _changes.delete(fieldName);
        }
      });

      // Store trimmed summary in projectInfo for API submission
      projectInfo.summaryAnalysis = summaryAnalysis.trim();
      dispatch(projectInfoResult(projectInfo));
      dispatch(selectFieldChanges([..._changes.values()]));

      incrementCounter?.();
    });
  }

  return projectInfo && fieldDefsMap?.size
    ? (
      <Grid item xs={12}>
        <form id="editProjectForm">
          <Grid item xs={12}>
            <h1 style={{ justifyContent: "center" }}>
              Edit Project
            </h1>
          </Grid>
          <Grid item xs={12}>
            <h6 style={{ justifyContent: "center" }}>
              Step 1 of {stepCount} - Edit Project Details
            </h6>
          </Grid>

          <div className="edit-project-table" style={{ marginTop: 30 }}>
            {fieldOrder.map(fieldName => {
              const fieldDef = fieldDefsMap.get(fieldName);

              // by design, will throw an exception if fieldDef is missing
              if (!fieldDef) {
                throw new Error(`Render fields: Missing field definition for ${fieldName}`);
              }
              
              if (fieldName === "styleComments" && !showStyleComments) {
                return <React.Fragment key={fieldName}></React.Fragment>;
              }

              return (
                <div key={fieldName} className="edit-project-cell">
                  <label>{fieldDef.label} <RequiredDocsTooltip fieldname={fieldName} /></label>
                  <div className="edit-project-field" onClick={() => {!changes.has(fieldName) && startEditing(fieldName)}}>
                    <EditableRequestField
                      fieldDef={fieldDef}
                      fieldValue={changes.has(fieldName) ? changes.get(fieldName).newValue : projectInfo[fieldName]}
                      isEditing={changes.has(fieldName)}
                      className={changes.has(fieldName)
                        ? fieldDef.type === "text"
                          ? "request-field-input"
                          : ""
                        : "field-value"}
                      inputRef={
                        fieldName === "street" ? streetInputRef :
                        fieldName === "city" ? cityInputRef :
                        fieldName === "zip5" ? zip5InputRef :
                        undefined
                      }
                      onChanged={(modifiedValue, validationMessage) => handleChanged(fieldName, modifiedValue, validationMessage)}
                      onBlur={(validationMessage) => handleBlur(fieldName, validationMessage)}
                      onFocus={() => {}}
                    />
                    <div className="edit-controls" style={{paddingLeft: 5}}>
                      {changes.has(fieldName) 
                        // ? <Undo onClick={() => cancelEditing(fieldName)} />
                        ? <Link style={{color: "#007bff"}} onClick={() => cancelEditing(fieldName)}>Cancel</Link>
                        : <FaPen className="feed-edit-icon" />}
                      </div>
                      <div className="field-error">
                        {changes.has(fieldName) && showValidation.has(fieldName) && validationErrors.has(fieldName) 
                          ? validationErrors.get(fieldName) 
                          : ""}
                      </div>
                  </div>
                </div>
              )}
            )}

            {!showStyleComments 
              ? (<div className="edit-project-cell spacer"></div>) 
              : (<></>)}

            <div style={{ marginTop: "20px", padding: "0 10px", width: "100%" }}>
              <h3 style={{ fontSize: "18px", fontWeight: "600", color: "#333", margin: "0 0 15px 0" }}>Summary & Analysis</h3>
              <p style={{ fontSize: "14px", color: "#666" }}>Provide a summary and analysis for this request:</p>
              <textarea
                rows="3"
                className="form-control"
                maxLength={SUMMARY_ANALYSIS_MAX_LENGTH}
                placeholder="Enter your summary and analysis here..."
                value={summaryAnalysis}
                onChange={e => setSummaryAnalysis(e.target.value)}
              />
              <div style={{ textAlign: "right", fontSize: "12px", color: "#666", marginTop: "4px" }}>
                {summaryAnalysis.length}/{SUMMARY_ANALYSIS_MAX_LENGTH}
              </div>
            </div>
          </div>

          <div className="stepper-controls" style={{margin: "40px 0 5px", flexWrap: "wrap", maxWidth: "unset"}}>
            <div className="edit-project-cell spacer"></div>
            <div className="edit-project-cell" style={{display: "flex", flex: "1", flexDirection: "row", justifyContent: "flex-end"}}>
              <Button
                type="submit"
                className={
                  "savebtn " + ( 
                    (!hasChanges || validationErrors.size) 
                      ? "disabled" 
                      : ""
                )}
                style={{
                  height: "40px",
                  width: "150px",
                  textTransform: "unset",
                  display: "flex",
                  justifyContent: "center",
                  alignSelf: "flex-end",
                }}
                onSubmit={handleSubmit}
                onClick={handleSubmit}
                form="editProjectForm"
              >
                Continue
              </Button>
            </div>
            {isSubmitted && !hasChanges
              ? <div className="field-error" style={{marginTop: 10, textAlign: "right"}}>Please make at least one change before continuing.</div>
              : isSubmitted && validationErrors.size
              ? <div className="field-error" style={{marginTop: 10, textAlign: "right"}}>Please ensure all fields are valid before continuing.</div>
              : <></>}
          </div>
        </form>
      </Grid>
  ) 
  : (<></>);
};